import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { handleActionError } from "@/lib/errors";
import {
  useStockTracking,
  useUntrackStock,
  useUpsertStockTracking
} from "@/queries/customer/stock-tracking";
import { toast } from "@medusajs/ui";
import { Boxes, PackageSearch, Plus } from "lucide-react";
import { memo, useCallback, useState } from "react";

import { AddProductDialog } from "./AddProductDialog";
import { StockItemRow, type StockItemSave } from "./StockItemRow";
import { TrackFromOrdersDialog } from "./TrackFromOrdersDialog";

/**
 * "My Stock" (#71): the salon's opt-in stock-tracking editor. Lists tracked
 * variants with inline reorder-level / on-hand editing (optimistic via the
 * mutation hooks), flags items due to reorder, and seeds the cold start from
 * order history. The list is exactly what the salon entered - nothing is
 * fabricated.
 */
export const MyStockView = memo(function MyStockView() {
  const { data, isLoading, error } = useStockTracking();
  useReportLoadFailure(error, "my stock");
  const upsert = useUpsertStockTracking();
  const untrack = useUntrackStock();
  const [seedOpen, setSeedOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const items = data?.stock_items ?? [];
  const dueCount = data?.due_count ?? 0;

  const handleSave = useCallback(
    (variantId: string, next: StockItemSave) => {
      upsert.mutate(
        {
          variant_id: variantId,
          reorder_level: next.reorder_level,
          on_hand: next.on_hand
        },
        {
          onError: (err) =>
            toast.error(
              handleActionError(err, {
                boundary: "my stock save",
                fallback: "We couldn't save your stock count. Please try again."
              })
            )
        }
      );
    },
    [upsert]
  );

  const handleRemove = useCallback(
    (variantId: string, label: string) => {
      untrack.mutate(variantId, {
        onSuccess: () => toast.success(`Stopped tracking ${label}`),
        onError: (err) =>
          toast.error(
            handleActionError(err, {
              boundary: "my stock untrack",
              fallback:
                "We couldn't stop tracking this product. Please try again."
            })
          )
      });
    },
    [untrack]
  );

  return (
    <div>
      <header className="border-border flex flex-col gap-4 border-b p-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2
              className="text-xl font-semibold text-gray-900 dark:text-white"
              id="my-stock-heading"
            >
              My Stock
            </h2>
            {dueCount > 0 ? (
              <Badge variant="warning">{dueCount} due to reorder</Badge>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Track what&apos;s on your shelf and we&apos;ll flag items when they
            run low.
          </p>
        </div>
        {items.length > 0 ? (
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setAddOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Add a product
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setSeedOpen(true)}
            >
              <PackageSearch className="mr-2 h-4 w-4" aria-hidden="true" />
              Track from your orders
            </Button>
          </div>
        ) : null}
      </header>

      <section aria-labelledby="my-stock-heading" className="p-6">
        {isLoading ? (
          <ul className="space-y-3" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className="border-border rounded-lg border p-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-12 w-12 rounded-md" />
                  <div className="flex-1">
                    <Skeleton className="mb-2 h-5 w-40" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400"
          >
            We couldn&apos;t load your tracked stock. Please try again.
          </div>
        ) : items.length === 0 ? (
          <div
            className="flex min-h-[240px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-gray-700"
            role="status"
          >
            <Boxes
              className="mb-4 h-10 w-10 text-gray-400"
              aria-hidden="true"
            />
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              Nothing tracked yet
            </p>
            <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              Add the products you keep on your shelf and set a reorder level.
              We&apos;ll flag them the moment your count runs low - no more
              running out mid-appointment.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <Button type="button" onClick={() => setSeedOpen(true)}>
                <PackageSearch className="mr-2 h-4 w-4" aria-hidden="true" />
                Track from your orders
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Search the catalogue
              </Button>
            </div>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item.id}>
                <StockItemRow
                  item={item}
                  onSave={handleSave}
                  onRemove={handleRemove}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <TrackFromOrdersDialog open={seedOpen} onOpenChange={setSeedOpen} />
      <AddProductDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
});
